import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Calendar, CreditCard, Download, History, TrendingDown } from 'lucide-react';
import { Badge, Button, Card, EmptyState, MetricCard, PageHeader, SkeletonKpiCard, TBody, TD, TH, THead, TR, Table } from '@/components/ui';
import { api } from '@/lib/api/client';
import { isApiError, unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import { downloadCsv } from '@/lib/utils/csvExport';
import type { ResponseEnvelope } from '@/types/api';

interface Subscription {
  id: string | null;
  clientId: string;
  packageName: string | null;
  startingBalance: number;
  currentBalance: number;
  usedBalance: number;
  periodStart: string | null;
  periodEnd: string | null;
  expiryDate: string | null;
  daysUntilExpiry: number;
  status: string | null;
  createdBy: string | null;
  createdAt: string | null;
}

interface BalanceTransaction {
  id: string;
  clientId: string;
  serviceType: string | null;
  amountDeducted: number;
  previousBalance: number;
  newBalance: number;
  requestId: string | null;
  createdAt: string;
}

async function fetchSubscription(): Promise<Subscription> {
  const res = await api.get<ResponseEnvelope<Subscription>>(Endpoints.me.subscription);
  return unwrap(res);
}
async function fetchBalanceHistory(): Promise<BalanceTransaction[]> {
  const res = await api.get<ResponseEnvelope<BalanceTransaction[]>>(`${Endpoints.me.balanceHistory}?limit=30`);
  return unwrap(res);
}

function statusTone(status: string | null): 'success' | 'danger' | 'warning' | 'neutral' {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':      return 'success';
    case 'EXPIRED':     return 'danger';
    case 'SUSPENDED':   return 'warning';
    case 'LOW_BALANCE': return 'warning';
    default:            return 'neutral';
  }
}

const SERVICE_LABELS: Record<string, string> = {
  VERIFY: 'Verify', VERIFY_N: 'Verify N', VERIFY_N_PLUS: 'Verify N+',
  OPPORTUNITY: 'Signal', SHIELD: 'Shield', DETECT: 'Detect', TRUST: 'Trust',
};

function serviceLabel(t: string | null): string {
  return t ? (SERVICE_LABELS[t.toUpperCase()] ?? t) : 'Verification';
}

export function ClientSubscriptionPage() {
  const [isExporting, setIsExporting] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['me', 'subscription'],
    queryFn: fetchSubscription,
    refetchInterval: 30_000,
  });
  const historyQuery = useQuery({
    queryKey: ['me', 'balance-history'],
    queryFn: fetchBalanceHistory,
  });

  const sub = data;
  const history = historyQuery.data ?? [];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Subscription & Balance" subtitle="Your current plan, credit usage, and deduction history." />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0,1,2,3].map((i) => <SkeletonKpiCard key={i} />)}
        </div>
      </div>
    );
  }

  if (isError || !sub) {
    return (
      <div className="space-y-6">
        <PageHeader title="Subscription & Balance" subtitle="Your current plan, credit usage, and deduction history." />
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 dark:border-rose-500/20 dark:bg-rose-500/10">
          <div className="flex items-center gap-2 text-rose-700 dark:text-rose-300">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <p className="text-sm">{isApiError(error) ? error.firstErrorMessage : 'Could not load subscription.'}</p>
          </div>
        </div>
      </div>
    );
  }

  const usedPct = sub.startingBalance > 0 ? Math.min(100, (sub.usedBalance / sub.startingBalance) * 100) : 0;
  const balanceExpiry = sub.expiryDate ? new Date(sub.expiryDate) : null;
  const isExpired = balanceExpiry != null && balanceExpiry < new Date();
  const daysLeft = sub.daysUntilExpiry;

  const avgDeduction = history.length > 0
    ? (history.reduce((acc, tx) => acc + tx.amountDeducted, 0) / history.length).toFixed(1)
    : null;
  const dailyRate = history.length >= 2
    ? (() => {
        const last = history[history.length - 1];
        const first = history[0];
        if (!last || !first) return null;
        const span = Math.max(1, (new Date(first.createdAt).getTime() - new Date(last.createdAt).getTime()) / 86_400_000);
        return Math.round(history.length / span);
      })()
    : null;
  const projectedDays = dailyRate && dailyRate > 0 && sub.currentBalance > 0
    ? Math.floor(sub.currentBalance / dailyRate)
    : null;

  const gradientForPct = usedPct > 80
    ? 'linear-gradient(90deg,#f87171,#ef4444)'
    : usedPct > 60
    ? 'linear-gradient(90deg,#fbbf24,#d97706)'
    : 'linear-gradient(90deg,#60a5fa,#2563eb)';

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Subscription & Balance"
        subtitle="Your current plan, credit usage, and deduction history."
        badge={<Badge tone={statusTone(sub.status)}>{sub.status ?? 'N/A'}</Badge>}
      />

      {isExpired && (
        <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 dark:border-rose-500/20 dark:bg-rose-500/10">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
          <div>
            <p className="text-sm font-semibold text-rose-700 dark:text-rose-300">Balance expired</p>
            <p className="mt-0.5 text-xs text-rose-600/80 dark:text-rose-400">
              Your verification balance expired on {balanceExpiry!.toLocaleDateString()}. Contact Authmatech to renew.
            </p>
          </div>
        </div>
      )}

      {/* Balance hero */}
      <div
        className="relative overflow-hidden rounded-2xl px-6 py-6"
        style={{
          background: sub.currentBalance <= 0 || isExpired
            ? 'linear-gradient(135deg, #3f0f0f 0%, #7f1d1d 100%)'
            : 'linear-gradient(135deg, #0f1f3d 0%, #1a3460 50%, #1d56d6 100%)',
        }}
      >
        <div className="pointer-events-none absolute -right-8 -top-8 h-48 w-48 rounded-full bg-white/[0.04]" aria-hidden />
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-widest text-white/50">{sub.packageName ?? 'Standard Plan'}</p>
          <p className="mt-2 text-6xl font-extrabold tracking-tight text-white">
            {sub.currentBalance.toLocaleString()}
          </p>
          <p className="mt-1 text-sm text-white/60">credits remaining</p>

          {/* Usage bar */}
          <div className="mt-5 space-y-1.5">
            <div className="flex justify-between text-xs text-white/50">
              <span>{sub.usedBalance.toLocaleString()} used</span>
              <span>{sub.startingBalance.toLocaleString()} total · {usedPct.toFixed(1)}% consumed</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${usedPct}%`, background: gradientForPct }}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            {balanceExpiry && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/70">
                <Calendar className="h-3 w-3" />
                {isExpired ? 'Expired' : `Expires in ${daysLeft}d — ${balanceExpiry.toLocaleDateString()}`}
              </span>
            )}
            {projectedDays != null && !isExpired && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/70">
                <TrendingDown className="h-3 w-3" />
                ~{projectedDays}d at current rate
              </span>
            )}
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Current balance" value={sub.currentBalance} icon={CreditCard} tone={sub.currentBalance <= 0 ? 'danger' : 'success'} />
        <MetricCard label="Used credits" value={sub.usedBalance} subtitle={avgDeduction ? `${avgDeduction} avg per call` : undefined} icon={TrendingDown} tone="warning" />
        <MetricCard label="Starting balance" value={sub.startingBalance} icon={CreditCard} tone="default" />
        <MetricCard label="Days until expiry" value={isExpired ? 'Expired' : `${daysLeft}d`} subtitle={balanceExpiry?.toLocaleDateString()} icon={Calendar} tone={isExpired ? 'danger' : daysLeft <= 14 ? 'warning' : 'default'} />
      </div>

      {/* Period details */}
      {(sub.periodStart || sub.periodEnd) && (
        <Card>
          <h2 className="text-sm font-semibold text-fg">Plan Period</h2>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            {sub.periodStart && (
              <div className="rounded-xl border border-border bg-surface-muted/40 p-3">
                <p className="text-xs font-medium text-fg-muted">Period Start</p>
                <p className="mt-1 text-sm font-semibold text-fg">{new Date(sub.periodStart).toLocaleDateString()}</p>
              </div>
            )}
            {sub.periodEnd && (
              <div className="rounded-xl border border-border bg-surface-muted/40 p-3">
                <p className="text-xs font-medium text-fg-muted">Period End</p>
                <p className="mt-1 text-sm font-semibold text-fg">{new Date(sub.periodEnd).toLocaleDateString()}</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Deduction history */}
      <Card noPadding>
        <div className="flex items-center gap-2 border-b border-border px-5 py-4">
          <History className="h-4 w-4 text-fg-muted" />
          <h2 className="text-sm font-semibold text-fg">Recent Deductions</h2>
          {history.length > 0 && (
            <span className="text-xs text-fg-subtle">{history.length} records</span>
          )}
          <div className="ml-auto">
            <Button
              variant="outline"
              size="sm"
              disabled={isExporting || history.length === 0}
              onClick={async () => {
                setIsExporting(true);
                try {
                  const headers = ['Date', 'Service', 'Deducted', 'Balance Before', 'Balance After'];
                  const rows = history.map((tx) => [
                    new Date(tx.createdAt).toLocaleString(),
                    serviceLabel(tx.serviceType),
                    tx.amountDeducted,
                    tx.previousBalance,
                    tx.newBalance,
                  ]);
                  downloadCsv(`balance-history-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
                } finally {
                  setIsExporting(false);
                }
              }}
            >
              <Download className="mr-1.5 h-3.5 w-3.5" />
              {isExporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          </div>
        </div>
        {historyQuery.isLoading ? (
          <div className="divide-y divide-border">
            {[0,1,2,3,4].map((i) => (
              <div key={i} className="animate-pulse flex items-center gap-4 px-5 py-3">
                <div className="h-3 w-28 rounded bg-surface-muted" />
                <div className="h-3 w-16 rounded bg-surface-muted ml-auto" />
                <div className="h-3 w-10 rounded bg-surface-muted" />
              </div>
            ))}
          </div>
        ) : historyQuery.isError ? (
          <div className="px-5 py-5"><EmptyState message="Could not load deduction history." /></div>
        ) : history.length === 0 ? (
          <div className="px-5 py-5"><EmptyState message="No deductions yet. History appears after your first API call." /></div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Date</TH>
                <TH>Service</TH>
                <TH className="text-right">Deducted</TH>
                <TH className="text-right">Before</TH>
                <TH className="text-right">After</TH>
              </TR>
            </THead>
            <TBody>
              {history.map((tx) => (
                <TR key={tx.id}>
                  <TD className="text-[11px] text-fg-muted">{new Date(tx.createdAt).toLocaleString()}</TD>
                  <TD><Badge tone="primary">{serviceLabel(tx.serviceType)}</Badge></TD>
                  <TD className="text-right font-bold text-rose-600 dark:text-rose-400">−{tx.amountDeducted}</TD>
                  <TD className="text-right text-xs text-fg-muted tabular-nums">{tx.previousBalance.toLocaleString()}</TD>
                  <TD className="text-right text-xs font-medium text-fg tabular-nums">{tx.newBalance.toLocaleString()}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
